// src/components/Alerts/ClientAlertsSection.js
// Displays list of clients with data freshness alerts
// Used in Firm Portal Dashboard and User Portal Manage Clients

import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Chip,
    Collapse,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Divider,
    Alert,
    CircularProgress,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import {
    Warning as WarningIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Close as CloseIcon,
    OpenInNew as OpenInNewIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { ALERT_TYPES, formatAlertDate } from '../../utils/alertUtils';
import { toast } from 'react-toastify';

/**
 * ClientAlertsSection - Display alerts for clients with outdated data
 * 
 * Props:
 * @param {array} alerts - Array of client alert objects from useClientAlerts
 * @param {boolean} loading - Loading state
 * @param {function} onDismiss - Callback to dismiss single alert (clientId, alertType)
 * @param {function} onDismissAll - Callback to dismiss all alerts for a client (clientId)
 * @param {function} onViewClient - Callback when "View Client" clicked (clientId)
 * @param {function} onRefetch - Callback to refresh alerts
 * @param {boolean} isFirmPortal - Whether this is firm portal (shows FCFO name)
 * @param {boolean} compact - Compact mode for dashboard widget
 * @param {number} maxItems - Max items to show in compact mode
 */
const ClientAlertsSection = ({
    alerts = [],
    loading = false,
    onDismiss,
    onDismissAll,
    onViewClient,
    onRefetch,
    isFirmPortal = false,
    compact = false,
    maxItems = 5
}) => {
    const [expanded, setExpanded] = useState(!compact);
    const [fcfoFilter, setFcfoFilter] = useState('all');
    const [alertTypeFilter, setAlertTypeFilter] = useState('all');
    const [dismissing, setDismissing] = useState({});

    // Get unique FCFOs for filter
    const uniqueFCFOs = [...new Set(alerts.map(a => a.fcfoName).filter(Boolean))];

    // Filter alerts
    const filteredAlerts = alerts.filter(alert => {
        if (fcfoFilter !== 'all' && alert.fcfoName !== fcfoFilter) return false;
        if (alertTypeFilter !== 'all') {
            const hasMatchingAlert = alert.alerts.some(a => a.type === alertTypeFilter);
            if (!hasMatchingAlert) return false;
        }
        return true;
    });

    const displayAlerts = compact ? filteredAlerts.slice(0, maxItems) : filteredAlerts;
    const hasMore = compact && filteredAlerts.length > maxItems;

    const handleDismiss = async (clientId, alertType) => {
        if (!onDismiss) return;
        
        const key = `${clientId}-${alertType}`;
        setDismissing(prev => ({ ...prev, [key]: true }));
        
        try {
            await onDismiss(clientId, alertType);
            toast.success('Alert dismissed');
        } catch (err) {
            toast.error('Failed to dismiss alert');
        } finally {
            setDismissing(prev => ({ ...prev, [key]: false }));
        }
    };

    const handleDismissAll = async (clientId) => {
        if (!onDismissAll) return;
        
        setDismissing(prev => ({ ...prev, [clientId]: true }));
        
        try {
            await onDismissAll(clientId);
            toast.success('All alerts dismissed for this client');
        } catch (err) {
            toast.error('Failed to dismiss alerts');
        } finally {
            setDismissing(prev => ({ ...prev, [clientId]: false }));
        }
    };

     const getAlertLabel = (type) => {
        switch (type) {
            case ALERT_TYPES.STRATEGIC_CONTEXT:
                return 'Strategic Context';
            case ALERT_TYPES.FINANCIALS:
                return 'Financials';
            case ALERT_TYPES.ASANA:
                return 'Asana';
            default:
                return 'Unknown';
        }
    };

     const getAlertColor = (daysSince, type) => {
        let threshold;
        switch (type) {
            case ALERT_TYPES.STRATEGIC_CONTEXT:
                threshold = 60;
                break;
            case ALERT_TYPES.FINANCIALS:
                threshold = 90;
                break;
            case ALERT_TYPES.ASANA:
                threshold = 30;
                break;
            default:
                threshold = 60;
        }
        const overdue = daysSince - threshold;
        if (overdue >= threshold) return 'error';
        return 'warning';
    };

    if (loading) {
        return (
            <Paper sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading alerts...</Typography>
                </Box>
            </Paper>
        );
    }

    const totalAlertCount = alerts.reduce((sum, a) => sum + a.alerts.length, 0);

    return (
        <Paper sx={{ p: 2, bgcolor: alerts.length > 0 ? 'warning.lighter' : 'inherit' }}>
            {/* Header */}
            <Box 
                display="flex" 
                justifyContent="space-between" 
                alignItems="center"
                sx={{ cursor: compact ? 'pointer' : 'default' }}
                onClick={() => compact && setExpanded(!expanded)}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <WarningIcon color={alerts.length > 0 ? 'warning' : 'disabled'} />
                    <Typography variant="h6">
                        {alerts.length > 0 
                            ? `${alerts.length} client${alerts.length !== 1 ? 's' : ''} need${alerts.length === 1 ? 's' : ''} attention`
                            : 'No alerts'
                        }
                    </Typography>
                    {totalAlertCount > 0 && (
                        <Chip 
                            label={`${totalAlertCount} alert${totalAlertCount !== 1 ? 's' : ''}`} 
                            size="small" 
                            color="warning"
                        />
                    )}
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                    {onRefetch && (
                        <Tooltip title="Refresh alerts">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRefetch(); }}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {compact && (
                        <IconButton size="small">
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    )}
                </Box>
            </Box>

            <Collapse in={expanded}>
                {alerts.length === 0 ? (
                    <Box mt={2}>
                        <Typography variant="body2" color="success.main">
                            ✓ All client data is up to date!
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Clients with outdated strategic context or financials that may affect AI recommendation quality.
                        </Typography>

                        {/* Filters (shown when expanded) */}
                        {expanded && isFirmPortal && (
                            <Box display="flex" gap={2} mt={2} mb={2}>
                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                    <InputLabel>Alert Type</InputLabel>
                                    <Select
                                        value={alertTypeFilter}
                                        label="Alert Type"
                                        onChange={(e) => setAlertTypeFilter(e.target.value)}
                                    >
                                        <MenuItem value="all">All Types</MenuItem>
                                        <MenuItem value={ALERT_TYPES.STRATEGIC_CONTEXT}>Strategic Context</MenuItem>
                                        <MenuItem value={ALERT_TYPES.FINANCIALS}>Financials</MenuItem>
                                        <MenuItem value={ALERT_TYPES.ASANA}>Asana</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                    <InputLabel>Alert Type</InputLabel>
                                    <Select
                                        value={alertTypeFilter}
                                        label="Alert Type"
                                        onChange={(e) => setAlertTypeFilter(e.target.value)}
                                    >
                                        <MenuItem value="all">All Types</MenuItem>
                                        <MenuItem value={ALERT_TYPES.STRATEGIC_CONTEXT}>Strategic Context</MenuItem>
                                        <MenuItem value={ALERT_TYPES.FINANCIALS}>Financials</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        {/* Alert List */}
                        <List dense disablePadding>
                            {displayAlerts.map((clientAlert, index) => (
                                <React.Fragment key={clientAlert.clientId}>
                                    {index > 0 && <Divider sx={{ my: 1 }} />}
                                    <ListItem
                                        alignItems="flex-start"
                                        sx={{ 
                                            flexDirection: 'column',
                                            bgcolor: 'background.paper',
                                            borderRadius: 1,
                                            mb: 1,
                                            p: 2
                                        }}
                                    >
                                        {/* Client Header */}
                                        <Box display="flex" justifyContent="space-between" width="100%" mb={1}>
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    {clientAlert.clientName}
                                                </Typography>
                                                {isFirmPortal && clientAlert.fcfoName && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Assigned to: {clientAlert.fcfoName}
                                                    </Typography>
                                                )}
                                                <Typography variant="caption" color="text.secondary">
                                                    {clientAlert.industry}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" gap={1}>
                                                {onDismissAll && !isFirmPortal && clientAlert.alerts.length > 1 && (
                                                    <Button
                                                        size="small"
                                                        color="inherit"
                                                        onClick={() => handleDismissAll(clientAlert.clientId)}
                                                        disabled={dismissing[clientAlert.clientId]}
                                                    >
                                                        Dismiss All
                                                    </Button>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Alerts for this client */}
                                        <Box display="flex" flexDirection="column" gap={1} width="100%">
                                            {clientAlert.alerts.map((alert, alertIndex) => (
                                                <Box 
                                                    key={alertIndex}
                                                    display="flex" 
                                                    alignItems="center" 
                                                    justifyContent="space-between"
                                                    sx={{ 
                                                        bgcolor: 'grey.50', 
                                                        p: 1, 
                                                        borderRadius: 1,
                                                        borderLeft: 3,
                                                        borderColor: `${getAlertColor(alert.daysSince, alert.type)}.main`
                                                    }}
                                                >
                                                    <Box>
                                                        <Chip
                                                            label={getAlertLabel(alert.type)}
                                                            size="small"
                                                            color={getAlertColor(alert.daysSince, alert.type)}
                                                            sx={{ mr: 1 }}
                                                        />
                                                        <Typography variant="body2" component="span">
                                                            Last updated: {formatAlertDate(alert.lastUpdated)}
                                                            {alert.lastUpdated && (
                                                                <Typography 
                                                                    component="span" 
                                                                    variant="body2" 
                                                                    color="text.secondary"
                                                                >
                                                                    {' '}({alert.daysSince} days ago)
                                                                </Typography>
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                    {onDismiss && !isFirmPortal && (
                                                        <Tooltip title="Dismiss alert">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDismiss(clientAlert.clientId, alert.type)}
                                                                disabled={dismissing[`${clientAlert.clientId}-${alert.type}`]}
                                                            >
                                                                {dismissing[`${clientAlert.clientId}-${alert.type}`] 
                                                                    ? <CircularProgress size={16} />
                                                                    : <CloseIcon fontSize="small" />
                                                                }
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    </ListItem>
                                </React.Fragment>
                            ))}
                        </List>

                        {/* Show more link */}
                        {hasMore && (
                            <Box mt={2} textAlign="center">
                                <Button size="small" onClick={() => setExpanded(true)}>
                                    View all {filteredAlerts.length} clients
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Collapse>
        </Paper>
    );
};

export default ClientAlertsSection;